// jalarConversion.ts — Lógica pura para "jalar" (copiar) tickets entre sorteos
// NORMAL (id interno 'TICA') y REVENTADO, en ambas direcciones.
//
// Modelo de datos (ver pos.types.ts):
//   - NORMAL:    { modalidad: 'NORMAL', numero, monto }                  → un único monto.
//   - REVENTADO: { modalidad: 'REVENTADO', numero, montoRev1, montoRev2 } → Rev1 = monto
//                 normal/directo, Rev2 = monto de reventado (0 si no aplica).
//
// Reglas de conversión al jalar:
//   - NORMAL → REVENTADO: el monto normal pasa como jugada principal (Rev1).
//     NO se inventa monto de reventado: Rev2 queda en 0.
//   - REVENTADO → NORMAL: solo se conserva el monto normal (Rev1). El monto de
//     reventado (Rev2) se EXCLUYE por completo, porque un sorteo normal maneja un
//     único monto. Las líneas que solo tienen reventado (Rev1 = 0) se descartan
//     porque no aportan monto normal.
//   - Mismo tipo origen/destino: se devuelven las jugadas sin cambios.
//
// Alcance: SOLO el par NORMAL ('TICA') ↔ REVENTADO. Cualquier otra combinación de
// tipos (MONAZOS, FECHAS, PARLEY, MEGA, etc.) se considera NO convertible y debe ser
// bloqueada por el llamador. La regla se aplica línea por línea.

import type { SorteoType, TicketItemData } from '../types/pos.types';

// Re-exportamos el tipo para que consumidores (y tests) puedan importarlo desde aquí.
export type { TicketItemData } from '../types/pos.types';

// Tipos de sorteo entre los que se permite jalar/convertir jugadas.
export const SORTEOS_CONVERTIBLES: SorteoType[] = ['TICA', 'REVENTADO'];

/**
 * Indica si se puede jalar un ticket de un sorteo de tipo `origen` hacia uno de tipo
 * `destino`. Solo es cierto para el par NORMAL ('TICA') ↔ REVENTADO (en cualquier orden,
 * incluyendo origen === destino dentro de ese par).
 */
export function sonSorteosConvertibles(origen: SorteoType, destino: SorteoType): boolean {
  return SORTEOS_CONVERTIBLES.includes(origen) && SORTEOS_CONVERTIBLES.includes(destino);
}

/**
 * Convierte las jugadas de un ticket al tipo del sorteo destino cuando se jala entre
 * NORMAL ('TICA') y REVENTADO. Aplica las reglas descritas arriba, línea por línea.
 *
 * - Si `origen === destino`, devuelve los items tal cual (sin clonar).
 * - Si la combinación no es soportada, devuelve un arreglo vacío para que el llamador
 *   lo trate como "no convertible".
 */
export function convertItemsEntreSorteos(
  items: TicketItemData[],
  origen: SorteoType,
  destino: SorteoType,
): TicketItemData[] {
  if (origen === destino) return items;

  // NORMAL (TICA) → REVENTADO: el monto normal se copia como Rev1; Rev2 = 0 (no se inventa).
  if (origen === 'TICA' && destino === 'REVENTADO') {
    const result: TicketItemData[] = [];
    for (const it of items) {
      if (it.modalidad !== 'NORMAL') continue;
      result.push({
        modalidad: 'REVENTADO',
        numero: it.numero,
        montoRev1: it.monto,
        montoRev2: 0,
      });
    }
    return result;
  }

  // REVENTADO → NORMAL: solo se conserva el monto normal (Rev1); se excluye Rev2 por completo.
  if (origen === 'REVENTADO' && destino === 'TICA') {
    const result: TicketItemData[] = [];
    for (const it of items) {
      if (it.modalidad !== 'REVENTADO') continue;
      // Excluir líneas que solo tienen reventado (Rev1 = 0): no aportan monto normal.
      if (it.montoRev1 <= 0) continue;
      result.push({
        modalidad: 'NORMAL',
        numero: it.numero,
        monto: it.montoRev1,
      });
    }
    return result;
  }

  // Combinación no soportada.
  return [];
}
